import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useDaoConfigStore } from '@/stores/daoConfigStore';
import { useProjectViews } from './useProjectViews';
import { getProjectParticipants } from '@/api/adapter/projectParticipants';
import { profileImageUrl } from './useProfile';
import type { ProfileReview } from '@/components/features/ratings/ReviewsList';
import { receivedEvaluations } from '@/lib/profile-reviews';

export function useProfileReviews(section: 'client' | 'developer') {
  const session = useAuthStore((state) => state.session);
  const projects = useProjectViews();
  const query = useQuery({
    queryKey: [
      'profile-reviews',
      session?.principalId,
      section,
      projects.data?.map((p) => [p.projectId, p.evaluations]),
    ],
    enabled: !!session && !!projects.data,
    retry: false,
    queryFn: async ({ signal }): Promise<ProfileReview[]> => {
      const reviews = await Promise.all(
        (projects.data ?? [])
          .filter((p) =>
            section === 'client'
              ? p.client === session!.accountId
              : p.client !== session!.accountId
          )
          .map(async (project) => {
            const votes = receivedEvaluations(project, session!.accountId);
            if (!votes.length) return [];
            const participants = await getProjectParticipants(
              project.projectId,
              signal
            );
            const people = [
              participants.client,
              participants.coordinator,
              ...participants.workers,
            ];
            return votes.map((vote) => {
              const author = people.find(
                (person) => person?.accountId === vote.author
              );
              const client = vote.author === project.client;
              const section = client ? 'client' : 'worker';
              return {
                id: project.projectId + ':' + vote.author,
                rating: vote.score,
                reviewerName:
                  author?.profiles?.[section]?.name ??
                  author?.displayName ??
                  'Project participant',
                reviewerRole: client
                  ? useDaoConfigStore.getState().config.terminology.clientRole || 'Client'
                  : vote.author === project.coordinator
                    ? useDaoConfigStore.getState().config.terminology.coordinatorRole || 'Coordinator'
                    : useDaoConfigStore.getState().config.terminology.workerRole || 'Developer',
                reviewerAvatar:
                  author?.profiles &&
                  (client ? author.clientImage : author.workerImage)
                    ? profileImageUrl(author.profiles.principalId, section)
                    : undefined,
              };
            });
          })
      );
      if (useAuthStore.getState().session?.principalId !== session?.principalId)
        throw new Error('Your account changed. Reload to view reviews.');
      return reviews.flat();
    },
  });
  return {
    ...query,
    isPending: projects.isPending || query.isPending,
    error: projects.error ?? query.error,
    refetch: async () => {
      await projects.refetch();
      await query.refetch();
    },
  };
}
